import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormField,
  Input,
  NumberInput,
  Select,
  SelectItem,
} from '@ntconsult/aluma-react';
import { useCreateShare } from '../../hooks/useCreateShare';
import type { LinkScope, ShareResourceType } from '../../types';

interface CreateLinkFormProps {
  resourceType: ShareResourceType;
  resourceId: string;
  onClose: () => void;
}

export function CreateLinkForm({
  resourceType,
  resourceId,
  onClose,
}: CreateLinkFormProps) {
  const { t } = useTranslation(['shares', 'common']);
  const [linkScope, setLinkScope] = useState<LinkScope>('anyone');
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [maxDownloads, setMaxDownloads] = useState<number | undefined>();

  const { mutate: createShare, isPending } = useCreateShare();

  const submit = () => {
    createShare(
      {
        kind: 'link',
        linkScope,
        resourceType,
        resourceId,
        password: password || undefined,
        expiresAt: expiresAt || undefined,
        maxDownloads,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="bg-muted rounded-lg p-4 mb-3 flex flex-col gap-3">
      <FormField label={t('links.scopeLabel')}>
        <Select
          value={linkScope}
          onValueChange={(v) => setLinkScope(v as LinkScope)}
        >
          <SelectItem value="anyone">{t('links.scope.anyone')}</SelectItem>
          <SelectItem value="org">{t('links.scope.org')}</SelectItem>
        </Select>
      </FormField>

      <FormField label={t('links.password')} hint={t('links.passwordHint')}>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />
      </FormField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FormField label={t('links.expiresAt')}>
          <Input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </FormField>

        <FormField label={t('links.maxDownloads')}>
          <NumberInput
            min={1}
            value={maxDownloads ?? null}
            onChange={(v) => setMaxDownloads(v ?? undefined)}
            placeholder={t('links.noLimit')}
          />
        </FormField>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button size="sm" onClick={submit} loading={isPending}>
          {t('links.createCta')}
        </Button>
      </div>
    </div>
  );
}
